class JVBoxContentsCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.items = [];
    this.viewport = null;
    this.track = null;
    this.prevButton = null;
    this.nextButton = null;
    this.dots = [];
    this.isDragging = false;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.handleResize = this.handleResize.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.viewport = this.querySelector('.jv-box-contents__carousel-viewport');
    this.track = this.querySelector('.jv-box-contents__carousel-track');
    if (!this.viewport || !this.track) return;

    this.items = Array.from(this.track.querySelectorAll('.jv-box-contents__item'));
    if (this.items.length === 0) return;

    this.prevButton = this.querySelector('.jv-box-contents__arrow--prev');
    this.nextButton = this.querySelector('.jv-box-contents__arrow--next');
    this.dots = Array.from(this.querySelectorAll('.jv-box-contents__dot'));

    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
    this.itemsPerScroll = Math.max(1, parseInt(this.dataset.itemsPerScroll, 10) || 4);

    this.setupEventListeners();
    this.setupResizeObserver();
    this.handleBreakpointChange();

    window.addEventListener('resize', this.handleResize, { passive: true });
    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
    } else {
      this.mobileQuery.addListener(this.handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mobileQuery.removeEventListener) {
      this.mobileQuery.removeEventListener('change', this.handleBreakpointChange);
    } else {
      this.mobileQuery.removeListener(this.handleBreakpointChange);
    }
  }

  isCarouselActive() {
    return this.mobileQuery.matches;
  }

  setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isCarouselActive()) {
        this.updateViewportHeight();
        this.scrollToIndex(this.currentIndex, false);
      }
    });

    for (const item of this.items) {
      this.resizeObserver.observe(item);
    }
  }

  handleBreakpointChange() {
    if (!this.isCarouselActive()) {
      this.stopAutoplay();
      this.viewport.scrollTop = 0;
      this.viewport.style.maxHeight = '';
      this.currentIndex = 0;
      return;
    }

    this.updateViewportHeight();
    this.updateControls();
    this.scrollToIndex(this.currentIndex, false);
    if (this.autoplay) this.startAutoplay();
  }

  updateViewportHeight() {
    if (!this.isCarouselActive() || this.items.length === 0) {
      this.viewport.style.maxHeight = '';
      return;
    }

    const gap = this.getGap();
    const visibleCount = Math.min(this.itemsPerScroll, this.items.length);
    let height = 0;

    for (let i = 0; i < visibleCount; i++) {
      height += this.items[i].offsetHeight;
      if (i < visibleCount - 1) {
        height += gap;
      }
    }

    this.viewport.style.maxHeight = `${height}px`;
  }

  getMaxIndex() {
    return Math.max(0, this.items.length - this.itemsPerScroll);
  }

  handleResize() {
    if (!this.isCarouselActive()) return;

    this.updateViewportHeight();
    const maxIndex = this.getMaxIndex();
    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex;
    }
    this.scrollToIndex(this.currentIndex, false);
    this.updateControls();
  }

  setupEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        if (!this.isCarouselActive()) return;
        this.goPrev();
        this.restartAutoplay();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        if (!this.isCarouselActive()) return;
        this.goNext();
        this.restartAutoplay();
      });
    }

    for (const dot of this.dots) {
      dot.addEventListener('click', () => {
        if (!this.isCarouselActive()) return;
        const index = parseInt(dot.dataset.index, 10);
        if (!Number.isNaN(index)) {
          this.scrollToIndex(index);
          this.restartAutoplay();
        }
      });
    }

    this.viewport.addEventListener('mouseenter', () => this.stopAutoplay());
    this.viewport.addEventListener('mouseleave', () => {
      if (this.autoplay && this.isCarouselActive()) this.startAutoplay();
    });

    let scrollTimeout;
    this.viewport.addEventListener('scroll', () => {
      if (!this.isCarouselActive() || this.isDragging) return;
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.syncIndexFromScroll(), 120);
    }, { passive: true });
  }

  getOffsetForIndex(index) {
    const gap = this.getGap();
    let offset = 0;

    for (let i = 0; i < index; i++) {
      offset += this.items[i].offsetHeight + gap;
    }

    return offset;
  }

  syncIndexFromScroll() {
    if (!this.items.length) return;

    const scrollTop = this.viewport.scrollTop;
    const gap = this.getGap();
    let offset = 0;
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    for (let i = 0; i < this.items.length; i++) {
      const distance = Math.abs(scrollTop - offset);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
      offset += this.items[i].offsetHeight + gap;
    }

    const pageStart = Math.floor(nearestIndex / this.itemsPerScroll) * this.itemsPerScroll;
    this.currentIndex = Math.max(0, Math.min(pageStart, this.getMaxIndex()));
    this.updateControls();
  }

  getGap() {
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.rowGap || styles.gap || '0');
    return Number.isNaN(gap) ? 0 : gap;
  }

  scrollToIndex(index, smooth = true) {
    if (!this.isCarouselActive()) return;

    const maxIndex = this.getMaxIndex();
    const pageStart = Math.floor(index / this.itemsPerScroll) * this.itemsPerScroll;
    this.currentIndex = Math.max(0, Math.min(pageStart, maxIndex));
    const top = this.getOffsetForIndex(this.currentIndex);

    this.isDragging = true;
    this.viewport.scrollTo({
      top,
      behavior: smooth ? 'smooth' : 'auto'
    });
    setTimeout(() => {
      this.isDragging = false;
    }, smooth ? 400 : 0);

    this.updateControls();
  }

  goNext() {
    const maxIndex = this.getMaxIndex();
    const nextIndex = this.currentIndex + this.itemsPerScroll;
    if (nextIndex > maxIndex) {
      this.scrollToIndex(0);
    } else {
      this.scrollToIndex(nextIndex);
    }
  }

  goPrev() {
    const prevIndex = this.currentIndex - this.itemsPerScroll;
    if (prevIndex < 0) {
      this.scrollToIndex(this.getMaxIndex());
    } else {
      this.scrollToIndex(prevIndex);
    }
  }

  updateControls() {
    const maxIndex = this.getMaxIndex();
    const showControls = this.isCarouselActive() && maxIndex > 0;

    if (this.prevButton) {
      this.prevButton.disabled = !showControls;
    }
    if (this.nextButton) {
      this.nextButton.disabled = !showControls;
    }

    if (this.dots.length) {
      const activePage = Math.floor(this.currentIndex / this.itemsPerScroll);
      for (const [i, dot] of this.dots.entries()) {
        const isActive = showControls && i === activePage;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      }
    }
  }

  startAutoplay() {
    if (!this.isCarouselActive() || !this.autoplay || this.getMaxIndex() === 0) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.goNext(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    if (this.autoplay && this.isCarouselActive()) this.startAutoplay();
  }
}

if (!customElements.get('jv-box-contents-carousel')) {
  customElements.define('jv-box-contents-carousel', JVBoxContentsCarousel);
}
